import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, MapPin, Monitor, Clock, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACK_FLAG = 'ghostcomm_activity_reviewed';

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

const truncateUA = (ua) => {
  if (!ua) return 'Unknown device';
  // Prefer a recognizable slice: "Chrome 120", "Safari iPhone", "curl/7"
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|curl|Playwright|Electron)[^\s;]*/i);
  if (m) return m[0].slice(0, 32);
  return ua.slice(0, 32);
};

const EventRow = ({ event, registration }) => (
  <div className="flex items-start gap-3 py-2 border-b border-slate-800 last:border-b-0" data-testid={`activity-row-${event.kind}`}>
    <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${registration ? 'bg-amber-400' : 'bg-emerald-400'}`} />
    <div className="flex-1 min-w-0 text-xs">
      <div className="flex items-center gap-2 text-slate-300">
        <span className="font-medium uppercase tracking-wide text-[10px] text-slate-500">
          {registration ? 'Registered' : 'Login'}
        </span>
        <Clock className="w-3 h-3 text-slate-500" />
        <span className="text-slate-400">{formatRelative(event.at)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-slate-400">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span className="font-mono text-[11px] text-slate-300">{event.ip || 'unknown ip'}</span>
          {event.country ? <span className="text-slate-500">·&nbsp;{event.country}</span> : null}
        </span>
        <span className="inline-flex items-center gap-1">
          <Monitor className="w-3 h-3" />
          <span className="text-[11px]">{truncateUA(event.user_agent)}</span>
        </span>
      </div>
    </div>
  </div>
);

export const SessionActivityBanner = () => {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && localStorage.getItem(ACK_FLAG) === '1'
  );
  const [lockOpen, setLockOpen] = useState(false);
  const [lockPassword, setLockPassword] = useState('');
  const [locking, setLocking] = useState(false);

  useEffect(() => {
    if (dismissed || !user) return;
    // Duress: never probe /auth/session-events. The endpoint
    // reveals server-side login history — exactly what the coerced
    // user is trying to keep hidden. Read sessionStorage directly
    // so we don't depend on a React state that might not be set
    // yet on the first render after duress login.
    if (typeof window !== 'undefined' &&
        sessionStorage.getItem('gs_duress_mode') === '1') return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/session-events`);
        setData(res.data);
      } catch (e) {
        // Silently fail — a missing activity banner must never break the dashboard.
      } finally {
        setLoading(false);
      }
    })();
  }, [dismissed, user]);

  const hasEvents = useMemo(() => {
    if (!data) return false;
    return Boolean(data.registration) || (data.recent_logins || []).length > 1;
  }, [data]);

  const handleAcknowledge = () => {
    localStorage.setItem(ACK_FLAG, '1');
    setDismissed(true);
  };

  const handleLock = async () => {
    if (!lockPassword) {
      toast.error('Enter your password to confirm.');
      return;
    }
    setLocking(true);
    try {
      await axios.post(`${API}/auth/lock-account`, { password: lockPassword });
      toast.success('Account locked. Signing you out…');
      setTimeout(() => logout?.(), 800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not lock account.');
    } finally {
      setLocking(false);
    }
  };

  if (dismissed || loading || !hasEvents) return null;

  return (
    <>
      <div
        data-testid="session-activity-banner"
        className="fixed bottom-5 left-5 z-40 w-[360px] rounded-xl border border-slate-800 bg-[#0F1115] shadow-2xl overflow-hidden"
        role="status"
      >
        <div className="p-4 pb-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-slate-50 leading-tight">
                Was this you?
              </h4>
              <p className="text-xs text-slate-400 mt-0.5">
                Cross-check recent account activity.
              </p>
            </div>
            <button
              onClick={handleAcknowledge}
              className="text-slate-500 hover:text-slate-300 flex-shrink-0"
              aria-label="Dismiss"
              data-testid="activity-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-4 pb-3 max-h-[280px] overflow-y-auto">
          {data?.registration ? (
            <EventRow event={data.registration} registration />
          ) : null}
          {(data?.recent_logins || []).map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </div>

        <div className="px-4 pb-4 pt-2 border-t border-slate-800 flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            onClick={handleAcknowledge}
            data-testid="activity-confirm-btn"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
            It was me
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="flex-1"
            onClick={() => setLockOpen(true)}
            data-testid="activity-lock-btn"
          >
            <Lock className="w-3.5 h-3.5 mr-1.5" />
            Wasn't me
          </Button>
        </div>
      </div>

      <Dialog open={lockOpen} onOpenChange={setLockOpen}>
        <DialogContent data-testid="lock-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Lock className="w-4 h-4" />
              Lock this account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will immediately revoke every session and disable your account.
              Re-enabling requires admin attention. Confirm your password to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="lock-pwd" className="text-xs text-slate-400">Password</Label>
            <Input
              id="lock-pwd"
              type="password"
              value={lockPassword}
              onChange={(e) => setLockPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLock(); }}
              data-testid="lock-password-input"
              autoComplete="current-password"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setLockOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleLock}
              disabled={locking || !lockPassword}
              data-testid="lock-confirm-btn"
            >
              {locking ? 'Locking…' : 'Lock my account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SessionActivityBanner;
